<section class="exec" [class.dm-dashboard]="role() === 'dm'" data-module="dashboard">
  <header class="exec-head">
    <div>
      <div class="title-line">
        <h1>{{ heading().title }}</h1>
      </div>
      <p>
        {{ officerName() }}
        <span class="last-sync">Updated {{ lastRefreshedAt() | date: 'HH:mm:ss' }}</span>
      </p>
    </div>
    <div class="btn-row">
      <button type="button" class="btn refresh-btn" [disabled]="refreshing()" (click)="refreshDashboard()">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M20 6v5h-5M4 18v-5h5" />
          <path d="M18.5 9A7 7 0 0 0 6 6.5L4 9M5.5 15A7 7 0 0 0 18 17.5l2-2.5" />
        </svg>
        {{ refreshing() ? 'Refreshing…' : 'Refresh' }}
      </button>
      @for (a of primaryActions(); track a.path) {
        <a class="btn" [class.primary]="a.primary" [routerLink]="a.path">{{ a.label }}</a>
      }
    </div>
  </header>

  @if (deniedMessage()) {
    <p class="toast deny">{{ deniedMessage() }}</p>
  }

  <div class="kpi-row">
    @for (k of kpis(); track k.label) {
      <a
        class="kpi-card"
        [attr.data-tone]="k.tone"
        [routerLink]="k.path"
        [queryParams]="k.queryParams || null"
      >
        <span class="kpi-ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            @for (d of iconPaths(k.icon); track d) {
              <svg:path [attr.d]="d" />
            }
          </svg>
        </span>
        <div>
          <strong>{{ k.value }}</strong>
          <span>{{ k.label }}</span>
        </div>
      </a>
    }
  </div>

  @if (showAnalytics()) {
    <div class="insights-head">
      <div>
        <p class="insights-kicker">Live overview</p>
        <h2 class="section-title">Complaint insights</h2>
      </div>
      <span>Updated from current grievance records</span>
    </div>
    <div class="analytics">
      <section class="card chart-card trend-card">
        <div class="card-heading">
          <div>
            <h3>Monthly complaint trend</h3>
            <p class="card-subtitle">Complaints received over six months</p>
          </div>
        </div>
        <div class="vbars" role="img" aria-label="Monthly grievance intake">
          @for (m of monthTrend(); track m.key) {
            <div class="vbar" [attr.title]="m.label + ': ' + m.value + ' grievances'">
              <b>{{ m.value }}</b>
              <div class="stem" [style.height.%]="trendHeight(m.value)"></div>
              <span>{{ m.label }}</span>
            </div>
          }
        </div>
      </section>

      <section class="card chart-card status-card">
        <div class="card-heading">
          <div>
            <h3>{{ role() === 'dm' || role() === 'admin' ? 'Complaint types' : 'Complaint status' }}</h3>
            <p class="card-subtitle">
              {{ role() === 'dm' || role() === 'admin' ? 'Distribution by case type' : 'Current distribution by progress' }}
            </p>
          </div>
        </div>
        @if (scopedComplaints().length === 0) {
          <p class="muted empty">No complaints yet — distribution will appear after intake.</p>
        } @else {
          <div class="donut-wrap">
            <div class="donut-chart">
              <div class="donut" [style.background]="donutGradient()"></div>
              <div class="donut-center">
                <strong>{{ scopedComplaints().length }}</strong>
                <span>Total</span>
              </div>
            </div>
            <ul class="legend">
              @for (s of donutShare(); track s.label) {
                <li>
                  <i [style.background]="s.color"></i>
                  {{ s.label }}
                  <b>{{ s.pct }}%</b>
                </li>
              }
            </ul>
          </div>
        }
      </section>

      <section class="card chart-card department-card">
        <div class="card-heading">
          <div>
            <h3>Department workload</h3>
            <p class="card-subtitle">Open and resolved complaints</p>
          </div>
          <div class="chart-key" aria-label="Chart legend">
            <span><i class="key-open"></i>Open</span>
            <span><i class="key-resolved"></i>Resolved</span>
          </div>
        </div>
        @if (departmentBreakdown().length) {
          <div class="bar-chart">
            @for (d of departmentBreakdown(); track d.department) {
              <div class="bar-row">
                <span>{{ d.department }}</span>
                <div class="bar-track">
                  <div class="bar-fill bar-open" [style.width.%]="departmentBarWidth(d.open)"></div>
                  <div class="bar-fill bar-resolved" [style.width.%]="departmentBarWidth(d.resolved)"></div>
                </div>
                <span>{{ d.open + d.resolved }}</span>
              </div>
            }
          </div>
        } @else {
          <p class="muted empty">No department data yet.</p>
        }
      </section>
    </div>
  }

  @if (showActivityFeed()) {
    <section class="card activity-card">
      <div class="card-toolbar">
        <div>
          <h3>Operational timeline</h3>
        </div>
        <div class="activity-filters" role="group" aria-label="Filter activity">
          @for (f of activityFilters; track f) {
            <button
              type="button"
              [class.active]="activityFilter() === f"
              (click)="setActivityFilter(f)"
            >
              {{ f }}
            </button>
          }
        </div>
      </div>

      @if (activityFeed().length === 0) {
        <p class="muted empty">No activity available for this filter.</p>
      } @else {
        <ol class="activity-timeline">
          @for (event of activityFeed(); track event.id) {
            <li [attr.data-kind]="event.kind">
              <span class="timeline-dot" aria-hidden="true"></span>
              <time>{{ event.at | date: 'd MMM y, HH:mm' }}</time>
              <a [routerLink]="event.path">
                <strong>{{ event.title }}</strong>
                <span>{{ event.body }}</span>
              </a>
              <span class="event-kind">{{ event.kind }}</span>
            </li>
          }
        </ol>
      }
    </section>
  }

  @if (showAdminRecentTabs()) {
    <section class="card table-card admin-recent-tabs">
      <div class="card-toolbar">
        <div class="admin-tab-bar" role="tablist" aria-label="Recent activity">
          <button
            type="button"
            role="tab"
            class="admin-tab"
            [class.active]="adminRecentTab() === 'complaints'"
            [attr.aria-selected]="adminRecentTab() === 'complaints'"
            (click)="setAdminRecentTab('complaints')"
          >
            Recent complaints
          </button>
          <button
            type="button"
            role="tab"
            class="admin-tab"
            [class.active]="adminRecentTab() === 'documents'"
            [attr.aria-selected]="adminRecentTab() === 'documents'"
            (click)="setAdminRecentTab('documents')"
          >
            Recent documents
          </button>
        </div>
        @if (adminRecentTab() === 'complaints') {
          <a routerLink="/complaints" class="link">View all</a>
        } @else {
          <a routerLink="/document-ocr/documents" class="link">View all</a>
        }
      </div>

      @if (adminRecentTab() === 'complaints') {
        @if (recent().length === 0) {
          <p class="muted empty">No grievances yet.</p>
        } @else {
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Case ID</th>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                @for (item of recent(); track item.id) {
                  <tr
                    class="click-row"
                    [routerLink]="['/complaints', item.id]"
                    tabindex="0"
                    role="link"
                  >
                    <td><strong>{{ item.trackingId }}</strong></td>
                    <td>{{ item.complainantName || item.citizenName || '—' }}</td>
                    <td>{{ item.subject }}</td>
                    <td>{{ item.updatedAt | date: 'd MMM' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      } @else {
        @if (recentDocuments().length === 0) {
          <p class="muted empty">No scans uploaded yet — capture one to get started.</p>
        } @else {
          <ol class="activity-timeline">
            @for (doc of recentDocuments(); track doc.key) {
              <li [attr.data-kind]="doc.state">
                <span class="timeline-dot" aria-hidden="true"></span>
                <time>{{ doc.updatedAt | date: 'd MMM y, HH:mm' }}</time>
                <a [routerLink]="doc.path">
                  <strong>{{ doc.title }}</strong>
                  <span>{{ doc.subtitle }}</span>
                </a>
                <span class="event-kind">{{ doc.state }}</span>
              </li>
            }
          </ol>
        }
      }
    </section>
  }

  @if (showDocumentActivity()) {
    <section class="card activity-card">
      <div class="card-toolbar">
        <h3>Recent documents</h3>
        <a routerLink="/document-ocr/documents" class="link">View all</a>
      </div>

      @if (recentDocuments().length === 0) {
        <p class="muted empty">No scans uploaded yet — capture one to get started.</p>
      } @else {
        <ol class="activity-timeline">
          @for (doc of recentDocuments(); track doc.key) {
            <li [attr.data-kind]="doc.state">
              <span class="timeline-dot" aria-hidden="true"></span>
              <time>{{ doc.updatedAt | date: 'd MMM y, HH:mm' }}</time>
              <a [routerLink]="doc.path">
                <strong>{{ doc.title }}</strong>
                <span>{{ doc.subtitle }}</span>
              </a>
              <span class="event-kind">{{ doc.state }}</span>
            </li>
          }
        </ol>
      }
    </section>
  }

  @if (showComplaintsPanel()) {
    <section class="card table-card">
      <div class="card-toolbar">
        <h3>
          @if (isInvestigator()) {
            My assigned complaints
          } @else if (role() === 'dm') {
            Recent complaints
          } @else {
            Recent grievances
          }
        </h3>
        <div class="toolbar-actions">
          @if (role() !== 'dm') {
            <div class="view-toggle" role="group" aria-label="View mode">
              <button type="button" class="toggle" [class.active]="viewMode() === 'table'" (click)="setView('table')">Table</button>
              <button type="button" class="toggle" [class.active]="viewMode() === 'cards'" (click)="setView('cards')">Cards</button>
            </div>
          }
          @if (canComplaints()) {
            <a routerLink="/complaints" class="link">View all</a>
          }
        </div>
      </div>

      @if (recent().length === 0) {
        <p class="muted empty">
          {{ isInvestigator() ? 'No complaints assigned to you yet.' : 'No grievances yet.' }}
        </p>
      } @else if (role() === 'dm' || viewMode() === 'table') {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Case ID</th>
                <th>Name</th>
                <th>Subject</th>
                @if (role() !== 'dm') {
                  <th>Status</th>
                }
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              @for (item of recent(); track item.id) {
                <tr
                  class="click-row"
                  [routerLink]="canComplaints() ? ['/complaints', item.id] : null"
                  [attr.tabindex]="canComplaints() ? 0 : null"
                  [attr.role]="canComplaints() ? 'link' : null"
                >
                  <td>
                    <strong>{{ item.trackingId }}</strong>
                  </td>
                  <td>{{ item.complainantName || item.citizenName || '—' }}</td>
                  <td>{{ item.subject }}</td>
                  @if (role() !== 'dm') {
                    <td><span class="pill">{{ item.status }}</span></td>
                  }
                  <td>{{ item.updatedAt | date: 'd MMM' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="card-grid">
          @for (item of recent(); track item.id) {
            <a
              class="complaint-card"
              [routerLink]="canComplaints() ? ['/complaints', item.id] : ['/dashboard']"
            >
              <div class="card-top">
                <strong>{{ item.trackingId }}</strong>
                <span class="pill">{{ item.status }}</span>
              </div>
              <p>{{ item.subject }}</p>
              <time>{{ item.updatedAt | date: 'd MMM y' }}</time>
            </a>
          }
        </div>
      }
    </section>
  }

  @if (showHearingsPanel()) {
    <section class="card">
      <div class="card-toolbar">
        <h3>{{ role() === 'peshkar' ? 'My hearing calendar' : 'Upcoming hearings' }}</h3>
        @if (canScheduling()) {
          <a routerLink="/scheduling" class="link">Calendar</a>
        }
      </div>
      @if (upcomingHearings().length === 0) {
        <p class="muted empty">No hearings scheduled.</p>
      } @else {
        <ul class="hearing-list">
          @for (h of upcomingHearings(); track h.id) {
            <li>
              <strong>{{ h.caseNo }}</strong>
              <p>{{ h.subject }}</p>
              <span>{{ h.scheduledAt | date: 'd MMM y, HH:mm' }} · {{ h.venue }}</span>
            </li>
          }
        </ul>
      }
    </section>
  }

  @if (showRosterPanel()) {
    <section class="card">
      <div class="card-toolbar">
        <h3>Roster snapshot</h3>
        @if (canRoster()) {
          <a routerLink="/roster" class="link">Manage roster</a>
        }
      </div>
      @if (roster().length === 0) {
        <p class="muted empty">No roster slots yet.</p>
      } @else {
        <ul class="hearing-list">
          @for (r of roster().slice(0, 5); track r.id) {
            <li>
              <strong>{{ r.name }}</strong>
              <p>{{ r.role }} · {{ r.department }}</p>
              <span>{{ r.availability }} · load {{ r.workload }}</span>
            </li>
          }
        </ul>
      }
    </section>
  }

  @if (showResolutionPanel()) {
    <section class="card">
      <div class="card-toolbar">
        <h3>{{ isInvestigator() ? 'My pending resolutions' : 'Pending resolutions' }}</h3>
        @if (canResolution()) {
          <a routerLink="/resolution" class="link">Open</a>
        }
      </div>
      @if (pendingResolutions().length === 0) {
        <p class="muted empty">No pending resolutions.</p>
      } @else {
        <ul class="hearing-list">
          @for (p of pendingResolutions().slice(0, 5); track p.id) {
            <li>
              <strong>{{ p.caseNo }}</strong>
              <p>{{ p.subject }}</p>
              <span>{{ p.officer }} · {{ p.notes || '—' }}</span>
            </li>
          }
        </ul>
      }
    </section>
  }

  @if (role() === 'peshkar' && canCases()) {
    <section class="card">
      <div class="card-toolbar">
        <h3>Case management</h3>
        <a routerLink="/cases" class="link">Open cases</a>
      </div>
    </section>
  }
</section>
